import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, CircularProgress, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo, useState } from "react";
import { INK } from "../../../../theme/tokens";
import { useThresholdStatus } from "../hooks/useThresholdStatus";
import { type ThresholdConfig, ThresholdStatus } from "../types";
import { ItemList } from "./ItemList";
import type { TableDiffItem } from "./type";

export type InHouseTableDiffChartProps = {
	title: string;
	subTitle: string;
	items: TableDiffItem[];
	isGoodFirst: boolean;
	threshold: ThresholdConfig;
	closestToTargetIsBest?: boolean; // default false - if true, checks |percentage - 100|
	higherIsBetter?: boolean; // default true - if false, lower percentages are better (ignored if closestToTargetIsBest=true)
	wantedWrapper?: (val: number) => React.ReactNode;
	actualWrapper?: (val: number) => React.ReactNode;
	maxVisibleItems?: number;
	onViewAll?: () => void;
	sortByBest?: boolean;
	badSortingOrder?: boolean;
	isLoading?: boolean;
	onDownload?: () => Promise<void>;
};

export const InHouseTableDiffChart: FC<InHouseTableDiffChartProps> = ({
	title,
	subTitle,
	items,
	isGoodFirst,
	threshold,
	closestToTargetIsBest = false,
	higherIsBetter = true,
	wantedWrapper,
	actualWrapper,
	maxVisibleItems = 5,
	onViewAll,
	sortByBest = true,
	badSortingOrder = false,
	isLoading = false,
	onDownload,
}) => {
	const [isDownloading, setIsDownloading] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const handleDownload = async () => {
		if (!onDownload || isDownloading) return;
		setIsDownloading(true);
		try {
			await onDownload();
		} finally {
			setIsDownloading(false);
		}
	};

	const getStatus = useThresholdStatus({
		threshold,
		mode: closestToTargetIsBest ? "closestToTarget" : higherIsBetter ? "higherIsBetter" : "lowerIsBetter",
	});

	const { visibleGoodItems, visibleBadItems, shouldShowViewAll } = useMemo(() => {
		const isItemGood = (item: TableDiffItem): boolean => {
			const percentage = item.percentageOfTarget ?? (item.actual / item.wanted) * 100;
			return getStatus(percentage) === ThresholdStatus.GOOD;
		};

		// Sort items based on sortOrder prop
		const sortItems = (itemsToSort: TableDiffItem[], bestFirst: boolean) => {
			return [...itemsToSort].sort((a, b) => {
				if (closestToTargetIsBest) {
					const aPercentage = a.percentageOfTarget ?? (a.actual / a.wanted) * 100;
					const bPercentage = b.percentageOfTarget ?? (b.actual / b.wanted) * 100;
					const aDiff = Math.abs(aPercentage - 100);
					const bDiff = Math.abs(bPercentage - 100);
					return bestFirst ? aDiff - bDiff : bDiff - aDiff;
				}

				const aValue = a.percentageOfTarget ?? a.actual;
				const bValue = b.percentageOfTarget ?? b.actual;
				return bestFirst ? bValue - aValue : aValue - bValue;
			});
		};

		const goodItems = sortItems(
			items.filter((item) => isItemGood(item)),
			sortByBest,
		);
		const badItems = sortItems(
			items.filter((item) => !isItemGood(item)),
			badSortingOrder,
		);

		const maxItemsPerColumn = Math.max(goodItems.length, badItems.length);

		return {
			visibleGoodItems: goodItems.slice(0, maxVisibleItems),
			visibleBadItems: badItems.slice(0, maxVisibleItems),
			shouldShowViewAll: maxItemsPerColumn > maxVisibleItems,
		};
	}, [items, sortByBest, badSortingOrder, maxVisibleItems, getStatus]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				padding: 1,
				width: "100%",
				height: "100%",
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box
					sx={{
						fontWeight: 700,
						fontSize: 20,
						color: INK.SECONDARY,
					}}
				>
					{title}
				</Box>
				{onDownload && (
					<Tooltip title="Download Excel">
						<IconButton
							onClick={handleDownload}
							disabled={isDownloading}
							sx={{
								opacity: isHovered ? 1 : 0,
								transition: "opacity 0.2s ease-in-out",
								padding: 0.5,
							}}
						>
							{isDownloading ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon sx={{ color: INK.SECONDARY }} />}
						</IconButton>
					</Tooltip>
				)}
			</Box>

			{isLoading ? (
				<Box sx={{ flex: 1 }}>
					<Skeleton variant="rectangular" width="100%" height="100%" sx={{ minHeight: "400px" }} />
				</Box>
			) : (
				<>
					<Box
						sx={{
							fontSize: 14,
							color: alpha(INK.SECONDARY, 0.5),
							marginBottom: 1,
						}}
					>
						{subTitle}
					</Box>
					<Box
						sx={{
							display: "flex",
							gap: 3,
							width: "100%",
							flex: 1,
							overflow: "hidden",
						}}
					>
						<ItemList
							items={isGoodFirst ? visibleGoodItems : visibleBadItems}
							threshold={threshold}
							closestToTargetIsBest={closestToTargetIsBest}
							higherIsBetter={higherIsBetter}
							wantedWrapper={wantedWrapper}
							actualWrapper={actualWrapper}
						/>
						<ItemList
							items={isGoodFirst ? visibleBadItems : visibleGoodItems}
							threshold={threshold}
							closestToTargetIsBest={closestToTargetIsBest}
							higherIsBetter={higherIsBetter}
							wantedWrapper={wantedWrapper}
							actualWrapper={actualWrapper}
						/>
					</Box>

					{/* View All button */}
					{shouldShowViewAll && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								marginTop: "auto",
							}}
						>
							<Typography
								onClick={onViewAll}
								sx={(theme) => ({
									fontSize: 14,
									fontWeight: 700,
									color: theme.palette.primary.main,
									cursor: "pointer",
									"&:hover": onViewAll
										? {
												textDecoration: "underline",
											}
										: {},
								})}
							>
								View All
							</Typography>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

export default InHouseTableDiffChart;
